/**
 * The marks that say what the AI changed (VALE-1174, pattern Y6 of VALE-1050).
 *
 * Y6's whole argument is that **the list is the annotation**: no gutter, no
 * rails, no coloured bars, and only new/updated fields rendered at all. So the
 * marks here are deliberately few —
 *
 * - the **value's own ink** carries new (info) vs updated (caution);
 * - a **create keeps one badge** on its row, because a create is the one state
 *   the fields cannot show on their own: it has no old value to contrast
 *   against. An update gets none — its amber values already say so.
 *
 * **No section legend** (VALE-1706). A `ChangeLegend` sat beside each compact
 * review heading naming the same two tags the rows below it carry. Placed on a
 * heading it read as a badge on the GROUP — as though the whole section were
 * new or updated — and on a narrow panel it landed within about 40px of the
 * first row's own badge, saying the same thing twice. The row's badge explains
 * it better, because it sits next to the thing it describes.
 */
import { cn } from '@/lib/utils';
import type { FieldChangeKind, ItemChangeKind } from './change-status';

/**
 * The tag fills, over ONE shared ink.
 *
 * `--change-tag-foreground` rather than each ramp's own `--<tone>-foreground`
 * (VALE-1196): the pair has to read as one system, and reaching the ink through
 * two different ramps made the same near-black look deliberate on blue and
 * mismatched on amber. One shared lightness, one ink — the `--avatar-foreground`
 * argument.
 *
 * **Still a token, never the literal.** The prototype hand-rolled
 * `text-[oklch(1_0_0)]`, which cannot follow the theme at all; this inverts with
 * the solid ramps the way the rest of the system does. The token's own docblock
 * carries the measured contrast and the owner decision behind its light half.
 */
const CHANGE_TAG_INK: Record<'new' | 'updated', string> = {
  new: 'bg-info text-change-tag-foreground',
  /*
    `--change-tag-updated`, not `--warning`: the plain warning fill is light
    enough that white ink measures 2.74:1 on it, under the 4.5:1 floor the
    design-token contrast guard holds this pair to. The tag takes the warning
    hue at the rung the avatar ramp already proved for white ink.
  */
  updated: 'bg-change-tag-updated text-change-tag-foreground'
};

/**
 * The tiny uppercase tag, at one size across the whole surface — a created
 * row's badge and a drawer's tag are the same object, not a pair of sizes.
 *
 * `text-9px` is the shipped treatment for this chip shape, not a new small
 * size: `balance-sheet/current-view/render-amount.tsx` renders the identical
 * geometry with the success ramp. The 12px label floor (LF-5859) governs
 * **labels**; this is a tag, and re-opening its size here would put this
 * surface out of step with the chip it was copied from.
 */
export function ChangeTag({
  kind,
  className
}: {
  kind: FieldChangeKind;
  className?: string;
}) {
  /*
    An addition takes no tag (VALE-1951): its value is inked as new text, and
    the hover card states the note it is added after. A tag reading "added"
    said nothing a reviewer could act on.
  */
  if (kind === 'unchanged' || kind === 'added') return null;
  return (
    <span
      className={cn(
        'text-9px inline-flex shrink-0 items-center rounded px-1 py-px font-semibold tracking-wide uppercase',
        CHANGE_TAG_INK[kind],
        className
      )}
    >
      {kind}
    </span>
  );
}

/**
 * The item badge for a row.
 *
 * Only a create gets one. Round 55 of the spike removed the `n updates` chip
 * from updated rows: it was the loudest mark on the row while saying only what
 * the row's own amber values already said, and it competed with the create
 * badge sitting beside it.
 */
export function ItemChangeBadge({ kind }: { kind: ItemChangeKind }) {
  /*
    **Both kinds, not just `new`** (VALE-1181).

    This rendered nothing for an `updated` row, so a matched item the AI had
    overwritten carried no mark of its own: the changed FIELDS were inked in the
    block below, and the row line said only `Draft`. A reader scanning the list
    for what the AI touched had to open every row to find out, which is the one
    job the row-level mark exists to do — and the legend above the list promised
    an `UPDATED` colour that no row ever showed.

    It was `new`-only because the surface that introduced it had creates alone
    to state; the moment a surface renders both side by side, saying one and not
    the other is worse than saying neither.
  */
  if (kind === 'unchanged') return null;
  return <ChangeTag kind={kind} />;
}

/**
 * The value inks (the round-3 ruling of VALE-1050): a new field's value renders
 * in the info ink — the production enrichment treatment — and an updated
 * field's in the caution ink, so the values themselves say what happened.
 *
 * **They are their own tokens now** (VALE-1494), not the `-subtle-foreground`
 * chip inks they started as. Round 5 asked the blue to go lighter, and this ink
 * is body text on `--card` while `--info-subtle-foreground` is the ink on an
 * `--info-subtle` chip: two roles, two backdrops, two contrast measurements.
 * Lightening the shared token would have moved ~30 unrelated chips and broken
 * the pair the contrast gate asserts. `design-tokens-theme.css` carries the
 * measurement and how far the blue can go before it drops under the floor.
 */
export const NEW_VALUE_INK = 'text-change-value-new font-medium';
export const UPDATED_VALUE_INK = 'text-change-value-updated font-medium';

/**
 * One field's value, inked for its change kind, with the old value inline.
 *
 * The old value sits **beside** the new one with no `was ` prefix: the struck
 * ink already says which is which, so the word did the same job twice and a
 * second line cost height on every single conflict. It gives way first when
 * space is tight (`shrink-[100]`) — the new value is what the reviewer is
 * being asked about.
 *
 * **A rejected value stays on screen and goes grey** (VALE-1208). It is not
 * cleared and not replaced by a summary of itself: the reviewer needs to see
 * what they turned down in order to turn it back on, and greying it says "this
 * is not being included" in the same language the rejected ROW uses. The
 * popover stays live on it, offering the value back.
 */
/**
 * **How a superseded value reads, in one place** (VALE-1654).
 *
 * Muted, struck, small, and giving way first — it is the value being replaced,
 * so it must never win space from the one replacing it.
 *
 * **`min-w-14` is the part that had to be added.** `shrink-[100]` with
 * `min-w-0` gives way all the way to nothing, which at panel width is what a
 * long proposal does to it: the old value disappeared, taking with it the only
 * evidence on the row that there had been one. A floor makes it truncate
 * visibly instead, and the hover card carries the whole of it. 14 × 3.4px is
 * ~48px, enough for a few characters and the ellipsis at `text-xs`.
 *
 * Written once because it is written at three sites — a text row's two states
 * and a control row's — and a floor is worth nothing at whichever one is
 * missed. They differed already: one of the three carried `opacity-70` and the
 * others did not.
 */
export const SUPERSEDED_VALUE_INK =
  'text-muted-foreground text-xs line-through decoration-1';

/**
 * **The layout a superseded value takes when it shares ONE LINE with the value
 * replacing it** (VALE-1654), which is every case except a wrapping row —
 * develop's `wraps` branch gives that one `valueClass` instead.
 *
 * `min-w-14` is the part that had to be added. `shrink-[100]` with `min-w-0`
 * gives way all the way to nothing, which at panel width is what a long
 * proposal does to it: the old value disappeared, taking with it the only
 * evidence on the row that there had been one. A floor makes it truncate
 * visibly instead, and the hover card carries the whole of it. 14 × 3.4px is
 * ~48px, enough for a few characters and the ellipsis at `text-xs`.
 */
export const SUPERSEDED_VALUE_LINE = 'min-w-14 shrink-[100] truncate';

export function ChangedValue({
  kind,
  value,
  previous,
  reverted = false,
  wraps = false,
  showsRecordedNote = false
}: {
  kind: FieldChangeKind;
  /** What the row prints now. */
  value: string;
  /** The pre-AI value, for an update. */
  previous?: string;
  /** Rejected, shown, but greyed, and not being applied. */
  reverted?: boolean;
  /**
   * **The value runs to several lines instead of ellipsising** (VALE-1698).
   *
   * Off by default, and that default is the right one for every row the
   * treatment marked before this: a name, a BSB, a date, an amount, a status —
   * all short, all sitting beside a struck twin that has to stay on the same
   * line for the pair to read as one substitution.
   *
   * A **prose** row is the case that breaks it. AI Scribe's note for an
   * existing item is a sentence or two, and inked into a fixed line it read
   * `…confirm before distributi…`: a word cut in half, which says the app
   * failed rather than that the text continues. So the surface declares it —
   * the row's own `multiline`, which already declares "this field is prose"
   * and is what puts its editor in a textarea. VALE-1698 originally made it a
   * per-surface opt-in (`useAiReviewFieldMarking`'s `valueWraps`) because the
   * derivation it considered was `fullWidth`, which two shipped rows already
   * set (the contact's bank account, the inventory drawer's address) and would
   * have rewrapped. `multiline` does not: neither of those rows sets it.
   * VALE-1744 made the derivation structural once the notes moved onto shared
   * list rows that cannot know what they are carrying.
   */
  wraps?: boolean;
  /**
   * For an ADDITION: print the recorded note (`previous`) above it, as a
   * drawer does, rather than the muted `…` a compact row uses (VALE-1951).
   */
  showsRecordedNote?: boolean;
}) {
  /*
    `items-start` when wrapping: a multi-line value beside a one-line struck
    twin would otherwise centre the twin against the whole block of text.
  */
  /*
    **An addition to a recorded note** (VALE-1951). Nothing is struck, because
    nothing is being replaced; rejected, only the addition is.

    In a drawer it prints the note as it will be saved: the recorded note in
    ordinary ink, then the addition beneath it. On a compact row it prints the
    addition alone behind a muted `…`, which says the note carries on before
    it; the drawer is where the whole of it is read.
  */
  if (kind === 'added') {
    /* The updated ink: the record already holds a note, and it is changing. */
    const additionInk = reverted
      ? cn(SUPERSEDED_VALUE_INK, 'opacity-70')
      : UPDATED_VALUE_INK;
    if (showsRecordedNote) {
      return (
        <span className="flex min-w-0 flex-col break-words whitespace-pre-line">
          {!!previous && <span className="text-foreground">{previous}</span>}
          <span className={additionInk}>{value}</span>
        </span>
      );
    }
    return (
      <span className="min-w-0 break-words whitespace-pre-line">
        {!!previous && (
          <span className="text-muted-foreground" aria-hidden>
            …{' '}
          </span>
        )}
        <span className={additionInk}>{value}</span>
      </span>
    );
  }

  const rowClass = wraps
    ? 'flex min-w-0 items-start gap-1.5'
    : 'flex min-w-0 items-center gap-1.5';
  const valueClass = wraps
    ? 'min-w-0 break-words whitespace-pre-line'
    : 'min-w-0 truncate';
  /*
    **Rejected: the value that will APPLY reads as the value.** It used to grey
    the whole row, which made the surviving value look as excluded as the one
    that was turned down, but the record is going to hold it, so it is ordinary
    text. What is greyed and struck is the AI's suggestion, which is the part no
    longer being taken.
  */
  if (reverted) {
    return (
      <span className={rowClass}>
        {/* No placeholder when there is nothing to fall back to: a rejected NEW
            value leaves the field empty, and a `—` in its place read as a value
            the reviewer had chosen (VALE-1208). */}
        {!!previous && (
          <span className={cn('text-foreground', valueClass)}>{previous}</span>
        )}
        <span
          className={cn(
            SUPERSEDED_VALUE_INK,
            'opacity-70',
            wraps ? valueClass : SUPERSEDED_VALUE_LINE
          )}
        >
          {value}
        </span>
      </span>
    );
  }

  return (
    <span className={rowClass}>
      <span
        className={cn(
          valueClass,
          kind === 'updated' ? UPDATED_VALUE_INK : NEW_VALUE_INK
        )}
      >
        {value}
      </span>
      {kind === 'updated' && !!previous && (
        <span
          className={cn(
            SUPERSEDED_VALUE_INK,
            wraps ? valueClass : SUPERSEDED_VALUE_LINE
          )}
        >
          {previous}
        </span>
      )}
    </span>
  );
}

/**
 * The change ink, reaching a row that draws a CONTROL instead of text.
 *
 * A `boolean`'s lit segment sets its own `data-[state=on]:text-foreground`, so
 * the ink has to win on that same state, a plain text class on the track never
 * applies. Handed to `AttributeSpec.valueClassName`, which is the slot that
 * reaches a row's value whatever the row draws (VALE-1208).
 */
export function changeInkClassName(kind: FieldChangeKind): string | undefined {
  if (kind === 'unchanged') return undefined;
  /*
    **Whole class names, written out**, never assembled from a template.
    Tailwind only emits a class it can read as a literal in the source, so an
    interpolated `[&_[data-state=on]]:${ink}` produced a class that existed in
    the DOM and nowhere in the stylesheet: the segment and the pill stayed
    grey on exactly the rows this exists for (VALE-1208).

    Two targets, both reached by the caller putting this class ON the element
    that stands for the value: a `select`'s pill takes the plain text class; a
    segmented control's segment sets its own `data-[state=on]:text-foreground`,
    so the same ink is restated on that state to win there.
  */
  return kind === 'updated' || kind === 'added'
    ? 'text-change-value-updated font-medium data-[state=on]:text-change-value-updated'
    : 'text-change-value-new font-medium data-[state=on]:text-change-value-new';
}

/**
 * **How a REJECTED value reads on a row that draws a control** (VALE-1181).
 *
 * A text row has had this since VALE-1208 — {@link ChangedValue}'s `reverted`
 * branch prints the value that will apply as ordinary text and strikes the AI's
 * suggestion beside it. A control row had nothing: the pill, the lit segment and
 * the kept picker kept the change ink after being rejected, so a turned-down
 * value went on reading as the one about to be written, and the only thing that
 * changed on screen was the popover's own label.
 *
 * That is rule 8's rendering half, and it was missing on every row kind except
 * the simple one — on contacts as much as on inventory, which is where it was
 * noticed.
 *
 * The control draws its own value, so this is the whole of what can be said
 * about it from outside: struck and muted, with the applying value stated beside
 * it by the caller.
 */
export const REJECTED_VALUE_INK =
  /*
    **The element AND every descendant, and this is one constant now**
    (VALE-1375).

    `text-decoration` is drawn by an ancestor ACROSS its in-flow descendants, and
    is not drawn across an inline-block, a flex item, a float, or anything out of
    flow. A figure row draws its amount as a NODE — `money`,
    `valueWithConfirmation` and `marketFigure` each render the figure inside
    layout of their own — so a strike on the value cell muted the row and then
    stopped at the number: a rejected `DOD value` sat grey and unstruck beside a
    `Model` struck through properly. One state, drawn two ways.

    There used to be a second constant for the kept-control branch, on the
    reasoning that only foreign markup needed reaching into. That split is what
    made this take two attempts to fix: the descendant form was corrected first,
    which is the branch a picker takes and NOT the branch a figure takes, so the
    reported row went on greying. There is one answer to "how does a rejected
    value read", it cannot depend on how deeply a control happens to nest its
    text, and applying it directly to every descendant is layout-independent.
  */
  'text-muted-foreground line-through decoration-1 opacity-70 [&_*]:text-muted-foreground [&_*]:line-through [&_*]:decoration-1';

/**
 * The ink for a row that draws its own **kept control** (VALE-1181, rule 5b).
 *
 * A control sets its own colour — `InstitutionSearchField`'s resting button
 * carries `ATTRIBUTE_VALUE_INK` — so a colour inherited from a wrapper loses to
 * it and the institution's name stayed black on a row the treatment had marked.
 * A descendant selector wins on specificity (`.wrapper button` is 0,1,1 against
 * the button's own 0,1,0) without touching the control, which is the only way
 * in from outside: the treatment cannot edit foreign markup, and a `!important`
 * would take the disabled and hover states with it.
 *
 * **Whole class names, written out**, for {@link changeInkClassName}'s reason —
 * an interpolated arbitrary variant is a class Tailwind never emits.
 */
export function changeInkDescendantClassName(
  kind: FieldChangeKind
): string | undefined {
  if (kind === 'unchanged') return undefined;
  /* Every descendant, for {@link REJECTED_VALUE_INK}'s reason. */
  return kind === 'updated' || kind === 'added'
    ? '[&_*]:text-change-value-updated [&_*]:font-medium'
    : '[&_*]:text-change-value-new [&_*]:font-medium';
}

/**
 * The superseded value, beside a row that draws a CONTROL.
 *
 * A text row prints its old value inside {@link ChangedValue}; a `select` draws
 * a pill and a `boolean` draws a segmented control, so theirs has to sit outside
 * whatever the row drew. Same treatment either way, struck, muted, giving way
 * first, so the two read as one pattern (VALE-1208).
 */
export function PreviousValue({ value }: { value?: string }) {
  if (!value) return null;
  return (
    <span
      /*
        **Marks this as a NOTE beside the value, not the value** (VALE-1690).
        `ChangeAnchor`'s fill rule stretches whichever child stands for the
        row's value so a picker occupies its cell; it used to say "the first
        one", which stopped being true once a rejected row puts its applying
        value ahead of what the row drew. Excluding the notes by name is the
        same statement and does not depend on their order.
      */
      data-ai-change-note=""
      className={cn(SUPERSEDED_VALUE_INK, SUPERSEDED_VALUE_LINE)}
    >
      {value}
    </span>
  );
}
